import { useSerialIds } from "highcharts";
import React, { useEffect, useState, useContext } from "react";
import appFetch from "../appFetch";
import apiEndpoints from "../apiEndpoints";
import styled from "styled-components";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import { LockClosedIcon, UserIcon, MailIcon } from "@heroicons/react/solid";
import Spinner from "../loaders/Spinner";
import * as Yup from "yup";

import doubleBubbleBackground from "../assets/images/double-bubble-outline.png";
import LoginLogo from "../assets/images/login-logo.png";
import EfccLogo from "../logos/EfccLogo.png"; // optional, used as fallback for efcc logo
import ReactJson from "react-json-view";
import { setToLS } from "../util/storage";
import { Link, useHistory } from "react-router-dom";
import toast from "react-hot-toast";
import AppContext from "../contexts/AppContext";
import PhoneNumber from "../components/PhoneNumber";
import WithThemeWrapper from "../theme/WithThemeWrapper";

const LoginContainer = styled.div`
  background: #ffffff;
`;

const LoginArea = styled.div`
  &:before,
  &:after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: -2;
  }
  &:before {
    background: #fdf9eb;
  }
  &:after {
    background: url(${doubleBubbleBackground});
    background-blend-mode: multiply;
    opacity: 0.25;
    z-index: -1;
  }
`;

const LoginArea2 = styled.div`
  &:before,
  &:after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: -2;
  }
  &:before {
    background: #ffffff;
  }
  &:after {
    background: url(${doubleBubbleBackground});
    background-blend-mode: multiply;
    opacity: 0.25;
    z-index: -1;
  }
`;

/**
 * Unified Login component
 *
 * Added flow:
 *  - For specific efcc-* subs: submit -> getCompanies -> choose company (modal) -> login with Company_Id
 *  - Auto-login when only 1 company returned
 */
function Login({ theme, subs }) {
  const [processing, setProcessing] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [booknowLoading, setBookNowLoading] = useState(false);
  const [companies, setCompanies] = useState([]); // companies returned by handleGetCompany
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [pendingValues, setPendingValues] = useState(null); // store form values while user picks company
  const history = useHistory();
  const { context, setContext } = useContext(AppContext);

  const logo = (theme?.logo || [])[0] || LoginLogo;

  const initialValues = {
    userName: "",
    password: "",
    phoneNumber: "",
  };
  const validationSchema = Yup.object().shape({
    userName: Yup.string()
      .emailOrPhone("not a valid email or phone number")
      .required("field is required!"),
    password: Yup.string()
      .min(4, "Password has to be longer than 6 characters!")
      .required("Password is required!"),
  });

  // unchanged postData but it will accept Company_Id in values if present
  const postData = (values) =>
    new Promise((resolve, reject) => {
      setProcessing(true);
      let { userName } = values;
      userName = userName.includes("@")
        ? userName
        : Yup.string().appendCountryCode("+234").cast(userName);
      appFetch
        .post(`${apiEndpoints.login}?subs=${subs}`, {
          body: JSON.stringify({ ...values, userName }),
        })
        .then((res) => {
          console.log("value of response is", res);
          resolve(res);
        })
        .catch((err) => reject(err))
        .finally(() => setProcessing(false));
    });

  // unchanged handleGetCompany (kept with encodeURIComponent as before)
  const handleGetCompany = (values) =>
    new Promise((resolve, reject) => {
      setProcessing(true);

      let { userName } = values;
      userName = userName.includes("@")
        ? userName
        : Yup.string().appendCountryCode("+234").cast(userName);

      appFetch
        .get(
          `${apiEndpoints.getCompanies}?emailOrPhone=${encodeURIComponent(
            userName
          )}&subs=${encodeURIComponent(subs)}`
        )
        .then((res) => {
          console.log("value of response is", res);
          resolve(res);
        })
        .catch((err) => reject(err))
        .finally(() => setProcessing(false));
    });

  // Helper: run the login flow with toast notifications and original redirection/handling
  const loginWithToast = (values) => {
    // reuse the exact success handler from your earlier toast.promise block
    return toast.promise(postData(values), {
      loading: "Processing",
      success: (res) => {
        let changePassword = /yes/i.test(res?.changePassword);
        if (changePassword)
          setContext((prev) => ({ ...prev, token: res.token }));

        setTimeout(() => {
          if (!changePassword) {
            setToLS("token", res.token);
            setToLS("loginUrl", res.loginUrl);
          }

          const redirect = res?.homeUrl ?? "home";
          let link = changePassword ? "reset-password" : redirect;
          history.push(link);
        }, 500);
        return "Login Successful. Redirecting..";
      },
      error: (err) => err?.message ?? "Login failed! try again.",
    });
  };

  // Called by Formik onSubmit. For efcc subs we do the "continue" flow, otherwise we login directly.
  const efccSubs =
    subs === "efcc-real-estate" ||
    subs === "efcc-accountant" ||
    subs === "efcc-casino" ||
    subs === "efcc-dpms" ||
    subs === "efcc-law-firm" ||
    subs === "efcc-tcsp";

  const handleContinueFlow = async (values) => {
    try {
      setPendingValues(values);
      const res = await handleGetCompany(values);
      // res could be an array or object; normalize
      const returned = Array.isArray(res) ? res : res?.data ?? res ?? [];
      if (!Array.isArray(returned)) {
        toast.error("Unexpected response from company lookup.");
        return;
      }

      if (returned.length === 0) {
        toast.error("No companies found for this account.");
        return;
      }

      if (returned.length === 1) {
        // auto-login using the single company id
        const company = returned[0];
        await loginWithToast({ ...values, Company_Id: company.id.toString() });
        setPendingValues(null);
        return;
      }

      // multiple companies -> show modal for selection
      setCompanies(returned);
      setShowCompanyModal(true);
    } catch (err) {
      console.error("Error fetching companies:", err);
      toast.error(err?.message ?? "Failed to fetch companies");
      setPendingValues(null);
    }
  };

  // Called when user picks a company from the modal
  const handleCompanySelect = async (company) => {
    try {
      setShowCompanyModal(false);
      // pendingValues should exist, but guard
      const values = pendingValues ?? { userName: "", password: "" };
      await loginWithToast({ ...values, Company_Id: company.id.toString() });
    } catch (err) {
      console.error("Login error after company select:", err);
    } finally {
      setPendingValues(null);
      setCompanies([]);
      setShowCompanyModal(false);
    }
  };

  const handleSignupClick = async (event) => {
    try {
      setSignupLoading(true);
      const res = await appFetch.post(`${apiEndpoints.signup}?subs=${subs}`);
      console.log("signup response", res.token);
      await setToLS("token", res.token);
    } catch (error) {
      console.error("Error in signup API call:", error);
      toast.error("Signup API failed!");
    } finally {
      setSignupLoading(false);
      let link = "signup";
      history.push(link.replace(/\s/g, "-"));
    }
  };

  const handleBookNowClick = async (event) => {
    try {
      setBookNowLoading(true);
      const res = await appFetch.post(`${apiEndpoints.signup}?subs=${subs}`);
      console.log("bookNow response", res.token);
      await setToLS("token", res.token);
    } catch (error) {
      console.error("Error in signup API call:", error);
      toast.error("Signup API failed!");
    } finally {
      setBookNowLoading(false);
      history.push(`/${subs}/book-now`);
    }
  };

  // Shared Form JSX so we don't duplicate logic
  function renderForm() {
    return (
      <LoginContainer className="rounded-md shadow-md py-6 px-4 sm:px-6 lg:px-8 bg-white border">
        <h2 className="text-center text-2xl font-medium text-custom-black-100">
          Login
        </h2>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            if (efccSubs) {
              // step 1: call handleGetCompany and proceed to modal or auto-login
              handleContinueFlow(values);
            } else {
              // immediate login for non-efcc subs
              loginWithToast(values);
            }
          }}
        >
          {(props) => {
            return (
              <FormikForm className="mt-8 space-y-6">
                <input type="hidden" name="remember" defaultValue="true" />

                <div className="mb-2">
                  <label
                    htmlFor="userName"
                    className="relative text-gray-400 focus-within:text-gray-600 block"
                  >
                    <span className="absolute left-0 inset-y-0 flex items-center pl-3 z-10">
                      <UserIcon
                        className="h-5 w-5"
                        style={{ color: theme?.colors?.primary }}
                        aria-hidden="true"
                      />
                    </span>
                    <Field
                      id="userName"
                      name="userName"
                      type="text"
                      autoComplete="off"
                      className="pl-9 placeholder-custom-gray-100 placeholder-opacity-40 appearance-none relative block w-full px-3 py-2 border border-custom-gray-200 text-gray-500 rounded-md focus:outline-none sm:text-sm"
                      placeholder="email or phone"
                    />
                  </label>

                  <ErrorMessage
                    name="userName"
                    render={(msg) => (
                      <div className="text-xs text-red-600 mt-1">{msg}</div>
                    )}
                  />
                </div>

                <div className="mb-1">
                  <label
                    htmlFor="password"
                    className="relative text-gray-400 focus-within:text-gray-600 block"
                  >
                    <span className="absolute left-0 inset-y-0 flex items-center pl-3 z-10">
                      <LockClosedIcon
                        className="h-5 w-5"
                        style={{ color: theme?.colors?.primary }}
                        aria-hidden="true"
                      />
                    </span>
                    <Field
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="off"
                      className="pl-9 placeholder-custom-gray-100 placeholder-opacity-40 appearance-none relative block w-full px-3 py-2 border border-custom-gray-200 text-gray-500 rounded-md focus:outline-none sm:text-sm"
                      placeholder="password"
                    />
                  </label>
                  <ErrorMessage
                    name="password"
                    render={(msg) => (
                      <div className="text-xs text-red-600 mt-1">{msg}</div>
                    )}
                  />
                </div>

                <button
                  type="submit"
                  disabled={processing}
                  style={{ backgroundColor: theme?.colors?.primary }}
                  className="group uppercase relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white"
                >
                  {processing ? (
                    <Spinner />
                  ) : (
                    <>{efccSubs ? "Continue" : "Login"}</>
                  )}
                </button>
              </FormikForm>
            );
          }}
        </Formik>

        <div>
          {efccSubs && (
            <>
              <div className="flex items-center my-4">
                <div className="flex-grow border-t border-gray-300"></div>
                <span className="mx-4 text-gray-500">or</span>
                <div className="flex-grow border-t border-gray-300"></div>
              </div>

              <a
                onClick={handleSignupClick}
                role="button"
                style={{
                  borderColor: theme?.colors?.primary,
                  color: theme?.colors?.primary,
                }}
                className="group uppercase relative w-full flex justify-center py-2 px-4 border-2 border-primary text-sm font-medium rounded-md"
              >
                {signupLoading ? (
                  <Spinner fill={theme?.colors.primary} />
                ) : (
                  <>Sign up</>
                )}
              </a>
            </>
          )}

          {subs === "demo-two" && (
            <>
              <div className="flex items-center my-4">
                <div className="flex-grow border-t border-gray-300"></div>
                <span className="mx-4 text-gray-500">or</span>
                <div className="flex-grow border-t border-gray-300"></div>
              </div>

              <a
                onClick={handleBookNowClick}
                role="button"
                style={{
                  borderColor: theme?.colors?.primary,
                  color: theme?.colors?.primary,
                }}
                className="group uppercase relative w-full flex justify-center py-2 px-4 border-2 border-primary text-sm font-medium rounded-md"
              >
                {booknowLoading ? (
                  <Spinner fill={theme?.colors.primary} />
                ) : (
                  <>Book Now</>
                )}
              </a>
            </>
          )}

          <div className="flex items-center justify-center mt-4">
            <div className="text-sm text-custom-gray-200">
              Forgot password?
              <Link
                to="forgot-password"
                className="font-medium"
                style={{ color: theme?.colors?.primary }}
              >
                &nbsp;Reset here
              </Link>
            </div>
          </div>
        </div>
      </LoginContainer>
    );
  }

  function CompanyModal({ companies = [], onSelect, onClose }) {
    const [search, setSearch] = React.useState("");

    // simple local filter (case-insensitive)
    const filtered = React.useMemo(() => {
      const q = String(search ?? "")
        .trim()
        .toLowerCase();
      if (!q) return companies;
      return companies.filter((c) => (c?.name ?? "").toLowerCase().includes(q));
    }, [companies, search]);

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          className="absolute inset-0 bg-black opacity-40"
          onClick={onClose}
        />

        <div className="relative bg-white rounded-lg shadow-lg w-full max-w-md z-10 p-4">
          {/* header: title left, search right */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">Choose company</h3>

            <div className="ml-3 w-48">
              <div className="relative">
                <svg
                  className= "absolute right-[172px] top-1/2 -translate-y-1/2 text-gray-600 h-4 w-4 fill-current"
                  xmlns="http://www.w3.org/2000/svg"
                  x="0px"
                  y="0px"
                  viewBox="0 0 56.966 56.966"
                  width="512px"
                  height="512px"
                >
                  <path d="M55.146,51.887L41.588,37.786c3.486-4.144,5.396-9.358,5.396-14.786c0-12.682-10.318-23-23-23s-23,10.318-23,23  s10.318,23,23,23c4.761,0,9.298-1.436,13.177-4.162l13.661,14.208c0.571,0.593,1.339,0.92,2.162,0.92  c0.779,0,1.518-0.297,2.079-0.837C56.255,54.982,56.293,53.08,55.146,51.887z M23.984,6c9.374,0,17,7.626,17,17s-7.626,17-17,17  s-17-7.626-17-17S14.61,6,23.984,6z" />
                </svg>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search companies..."
                  className="w-full text-sm pl-6 pr-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  aria-label="Search companies"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded bg-gray-100"
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* count / feedback */}
          <div className="text-xs text-gray-500 mb-2">
            {filtered.length} {filtered.length === 1 ? "company" : "companies"}
          </div>

          {/* scrollable list */}
          <div className="overflow-auto w-full tranx-table scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-300 mt-2 max-h-64 space-y-2">
            {filtered.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500">
                No companies match "{search}"
              </div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  onClick={() => onSelect(c)}
                  className="w-full text-center px-4 py-3 border border-gray-200 rounded hover:bg-gray-50 flex flex-col items-center justify-center"
                >
                  <div className="font-medium">{c.name}</div>
                  {c.code && (
                    <div className="text-xs text-gray-500 mt-1">{c.code}</div>
                  )}
                </button>
              ))
            )}
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={onClose}
              style={{ backgroundColor: theme?.colors?.primary }}
              className="px-4 py-2 rounded border text-sm text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (
    subs === "efcc" ||
    subs === "efcc-real-estate" ||
    subs === "efcc-accountant" ||
    subs === "efcc-casino" ||
    subs === "efcc-dpms" ||
    subs === "efcc-law-firm" ||
    subs === "efcc-tcsp"
  ) {
    const remoteEfccLogoUrl = null;

    return (
      <LoginArea2 className="w-full h-full overflow-hidden">
        <div className="w-full h-full grid grid-cols-1 md:grid-cols-2">
          <div className="flex items-center justify-center h-full p-8 order-2 md:order-1">
            <div className="w-full  max-w-md">
              <img
                className="mx-auto h-10 w-auto block md:hidden"
                src={logo || EfccLogo}
                alt={theme?.name}
              />
              {renderForm()}
            </div>
          </div>

          <div
            className="hidden md:flex items-center justify-center p-0 h-full order-1 md:order-2"
            style={{
              backgroundColor: theme?.colors?.primary || "#b80a0a",
            }}
          >
            <div className="w-full h-full flex flex-col items-center justify-start relative">
              <div className="w-full flex items-start justify-center pt-8">
                <div className="text-center">
                  <h1
                    className="text-white leading-none"
                    style={{
                      fontFamily: "'Cinzel', serif",
                      fontWeight: 700,
                      fontSize: "8.5rem",
                      lineHeight: 1,
                      letterSpacing: "0.10em",
                      margin: 0,
                    }}
                  >
                    SCUML
                  </h1>

                  <div
                    className="text-white -mt-4 mr-3"
                    style={{
                      fontWeight: 500,
                      fontSize: "1.15rem",
                      letterSpacing: "0.06em",
                      wordSpacing: "0.3em",
                    }}
                  >
                    Special Control Unit Against Money Laundering
                  </div>
                </div>
              </div>

              <div className="flex-1 flex items-center justify-center w-full">
                <div className="relative">
                  <div
                    className="rounded-full overflow-hidden flex items-center justify-center shadow"
                    style={{ width: 480, height: 480 }}
                  >
                    <img
                      src={EfccLogo || remoteEfccLogoUrl || logo}
                      alt="EFCC logo"
                      className="object-cover w-full h-full bg-red-700"
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  position: "absolute",
                  top: "16%",
                  right: "50%",
                  transform: "translateX(50%) translateY(-50%)",
                }}
              />
            </div>
          </div>
        </div>

        {/* company modal */}
        {showCompanyModal && (
          <CompanyModal
            companies={companies}
            onSelect={handleCompanySelect}
            onClose={() => {
              setShowCompanyModal(false);
              setPendingValues(null);
              setCompanies([]);
            }}
          />
        )}
      </LoginArea2>
    );
  }

  return (
    <LoginArea className="min-h-screen h-full flex flex-col items-center justify-center">
      <div className="max-w-md w-full space-y-8 -mt-5 px-4">
        <div>
          <img className="mx-auto h-12 w-auto" src={logo} alt={theme?.name} />
        </div>

        <div>{/* The form container */}</div>

        <div>{renderForm()}</div>
      </div>
    </LoginArea>
  );
}

export default WithThemeWrapper(Login);
